/**
 * How a job bills the parts used on it (CONTEXT.md, "Pricing basis"; Inventory v2 Phase 1).
 * T&M bills every tracked part used at its price-book price; Fixed treats parts as cost, because
 * the customer agreed a price. Every service type, and so every job, starts Fixed.
 */
export const PRICING_BASES = ['fixed', 'tm'] as const;
export type PricingBasis = (typeof PRICING_BASES)[number];

export const PRICING_BASIS_LABEL: Record<PricingBasis, string> = {
  fixed: 'Fixed',
  tm: 'T&M',
};

/** Who set a job's basis: its service type when it was booked, or the office afterwards. */
export const PRICING_BASIS_SET_BY = ['type', 'office'] as const;
export type PricingBasisSetBy = (typeof PRICING_BASIS_SET_BY)[number];

/**
 * The one state the reconcile read gives each part used on a job (Phase 1 spec, "The reconcile
 * read"). Defined here, ahead of the slices that compute them, so every screen draws from one list.
 */
export const PART_BILLING_STATES = [
  'on_draft',
  'on_invoice',
  'credit_due',
  'waiting',
  'unbilled',
  'beyond_quote',
  'covered',
  'not_synced',
  'written_off',
] as const;
export type PartBillingState = (typeof PART_BILLING_STATES)[number];

/**
 * What became of a part's billing the moment it was used (Phase 1, "The sync on use", as ruled
 * 2026-10-06, "B"): waiting for the job's first invoice, which picks it up; unbilled, left to
 * bill until the office adds it (the job has an invoice already, draft or issued, or the pickup
 * is switched off); or cost only (a Fixed job). A use never puts a part on an invoice itself, and
 * answers one of these without ever failing for any of them.
 */
export const USE_BILLING = ['waiting', 'unbilled', 'cost_only'] as const;
export type UseBilling = (typeof USE_BILLING)[number];
